
import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Headset, User, Send, Smartphone } from 'lucide-react';
import { Order, SystemConfig, TextClip, Driver } from '../../types';
import { openWhatsAppText, hydrateTemplate } from '../../services/communicationService';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    order: Order;
    systemConfig?: SystemConfig;
    driver?: Driver;
    anchorRect: DOMRect | null;
}

export const CommunicationDropdown: React.FC<Props> = ({ isOpen, onClose, order, systemConfig, driver, anchorRect }) => {
    if (!isOpen || !anchorRect || !systemConfig) return null;

    const commConfig = systemConfig.communication;
    if (!commConfig) return null;

    const dispatcherClips = commConfig.dispatcherTextClips || [];
    const driverClips = commConfig.textClips || [];

    const handleSendMessage = (template: string) => {
        if (!order.phone) return;
        const hydrated = hydrateTemplate(template, order, driver || { name: 'seu entregador' }, systemConfig);
        openWhatsAppText(order.phone, hydrated);
        onClose();
    };

    // Alinhamento inteligente: evita sair da tela
    const top = anchorRect.bottom + window.scrollY + 8;
    const left = Math.min(anchorRect.left + window.scrollX, window.innerWidth - 260);

    return createPortal(
        <>
            <div className="fixed inset-0 z-[9998]" onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                style={{ top, left }}
                className="fixed z-[9999] w-64 bg-card-bg border border-border-dim rounded-xl shadow-[var(--shadow-lg)] overflow-hidden ring-1 ring-black/10"
            >
                <div className="p-3 border-b border-border-dim bg-gradient-to-r from-sal-cyber-teal/10 to-transparent flex items-center justify-between">
                    <div>
                        <h4 className="text-xs font-black text-text-primary uppercase tracking-widest">Atendimento</h4>
                        <p className="text-xs text-text-muted font-bold uppercase truncate max-w-[150px]">{order.customerName}</p>
                    </div>
                    <Smartphone className="w-3.5 h-3.5 text-sal-cyber-teal opacity-50" />
                </div>

                <div className="max-h-[350px] overflow-y-auto custom-scrollbar no-scrollbar">
                    <div className="p-1 px-2 mt-1">
                        <span className="text-xs font-black text-text-muted uppercase px-2 mb-1 block tracking-tighter">Mensagens de Suporte</span>
                        {dispatcherClips.map((clip: TextClip) => (
                            <button
                                key={clip.id}
                                onClick={() => handleSendMessage(clip.content)}
                                className="w-full text-left p-2.5 rounded-lg hover:bg-sal-cyber-teal/5 dark:hover:bg-sal-cyber-teal/10 group transition-all"
                            >
                                <div className="flex justify-between items-center mb-0.5">
                                    <span className="text-xs font-black text-sal-cyber-teal uppercase">{clip.scenario}</span>
                                    <Send className="w-3 h-3 text-icon-muted group-hover:text-sal-cyber-teal transition-colors" />
                                </div>
                                <p className="text-xs text-text-muted leading-tight line-clamp-2">
                                    {clip.content}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>

                {dispatcherClips.length === 0 && (
                    <div className="p-6 text-center">
                        <p className="text-xs text-slate-500 font-black uppercase">Sem templates</p>
                    </div>
                )}
            </motion.div>
        </>,
        document.body
    );
};
